/**
 * Where the Group Detail back arrow goes (ADO #1625, Lindsay 2026-09-08).
 *
 * The arrow hardcoded `/ops/groups`, so someone who opened a group from
 * Operation Daylight, My Groups, Transitions or Renewals landed on All Groups
 * and lost their place. `navigate(-1)` is not the fix: the detail page pushes a
 * history entry on every tab change (setActiveTab uses replace: false, on
 * purpose, so tab deep links are shareable and browser back walks the tabs), so
 * a history-relative back would step through the tabs before it ever left the
 * group.
 *
 * Instead OpsRootLayout records the last Ops route that was NOT a group detail
 * page, and the arrow reads it. Recording centrally is what makes this hold:
 * roughly twenty call sites open a group (plus the header search, which can
 * fire from any page in the module), and none of them have to know about it,
 * including the next one somebody adds. sessionStorage rather than component
 * state so a refresh on the detail page keeps the origin; per-tab so two tabs
 * do not fight over it.
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const STORAGE_KEY = 'ops:groupBackTarget';
/** A recorded path longer than this is stored without its query string. */
const MAX_PATH_LENGTH = 2000;

export interface GroupBackTarget {
  /** Absolute path to navigate to, query string included. */
  path: string;
  /** Reads after "Back to " in the tooltip. */
  label: string;
}

/**
 * `/groups/<id>` anywhere in the pathname: the detail page and its children
 * (face sheet, plan packet). Matches under both mounts, `/groups/<id>`
 * standalone and `/ops/groups/<id>` in the shell, same as the quick-note FAB.
 */
const GROUP_DETAIL_RE = /\/groups\/[^/]+/;

/**
 * Route suffix (pathname with the mount prefix stripped) to the words that
 * follow "Back to". Most specific first; the labels track the sidebar so the
 * tooltip names the page the way the nav does.
 */
const ROUTE_LABELS: ReadonlyArray<readonly [RegExp, string]> = [
  [/^\/operation-daylight\/team\//, 'the Daylight team'],
  [/^\/operation-daylight$/, 'Operation Daylight'],
  [/^\/my-groups$/, 'My Groups'],
  [/^\/my-tasks$/, 'Tasks & Alerts'],
  [/^\/groups$/, 'All Groups'],
  [/^\/groups-map$/, 'Groups Map'],
  [/^\/search$/, 'Advanced Search'],
  [/^\/intake$/, 'Intake'],
  [/^\/transitions\/[^/]+\/gantt$/, 'the transition plan'],
  [/^\/transitions$/, 'Transitions'],
  [/^\/implementations/, 'Transitions'],
  [/^\/impforms\/[^/]+\/review$/, 'the form review'],
  [/^\/impforms$/, 'Implementation Forms'],
  [/^\/renewals$/, 'Renewals'],
  [/^\/approvals$/, 'Approvals'],
  [/^\/data-opportunities$/, 'Data Opportunities'],
  [/^\/crm-matches$/, 'CRM Matches'],
  [/^\/managed-lists$/, 'Managed Lists'],
  [/^\/group-site-provisioning$/, 'Group Site Provisioning'],
  [/^\/guide/, 'the Ops guide'],
];

export function isGroupDetailPath(pathname: string): boolean {
  return GROUP_DETAIL_RE.test(pathname);
}

/**
 * The label for a pathname under the given mount. Unknown Ops routes get a
 * bare "Back", which still reads correctly in the tooltip.
 */
export function labelForPath(pathname: string, basePath: string): string {
  if (pathname === (basePath || '/')) return 'the Dashboard';
  const suffix = basePath && pathname.startsWith(basePath)
    ? pathname.slice(basePath.length)
    : pathname;
  for (const [re, label] of ROUTE_LABELS) {
    if (re.test(suffix)) return label;
  }
  return '';
}

/**
 * Records every Ops route that is not a group detail page, so the detail page
 * knows where the person came from. Called once, from OpsRootLayout.
 */
export function useRecordGroupBackTarget(basePath: string): void {
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (isGroupDetailPath(pathname)) return;
    const label = labelForPath(pathname, basePath);
    const withSearch = `${pathname}${search}`;
    const path = withSearch.length > MAX_PATH_LENGTH ? pathname : withSearch;
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ path, label }));
    } catch {
      // Private-mode or a full quota: the back arrow just falls back to
      // All Groups, which is where it went before this existed.
    }
  }, [pathname, search, basePath]);
}

/**
 * Resolves the back target for a group detail pathname. Falls back to the
 * module's All Groups list, which is the old hardcoded behavior and the right
 * answer for a deep link opened in a fresh tab.
 */
export function resolveGroupBackTarget(detailPathname: string): GroupBackTarget {
  const idx = detailPathname.indexOf('/groups/');
  const basePath = idx > 0 ? detailPathname.slice(0, idx) : '';
  const fallback: GroupBackTarget = { path: `${basePath}/groups`, label: 'All Groups' };
  let stored: unknown;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return fallback;
    stored = JSON.parse(raw);
  } catch {
    return fallback;
  }
  if (typeof stored !== 'object' || stored === null) return fallback;
  const { path, label } = stored as Record<string, unknown>;
  // Only same-origin absolute paths, never a protocol-relative `//host` one.
  if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//')) return fallback;
  if (isGroupDetailPath(path)) return fallback;
  return { path, label: typeof label === 'string' && label ? label : '' };
}

/**
 * The back target for the group detail page, captured on mount. Stable for the
 * life of the page: the recorder skips group detail routes, so nothing can
 * overwrite it while we are here, and switching tabs must not move the arrow.
 */
export function useGroupBackTarget(): GroupBackTarget {
  const { pathname } = useLocation();
  const [target] = useState(() => resolveGroupBackTarget(pathname));
  return target;
}
